/**
 * Session Store
 *
 * Auth-adjacent session data: current user, department context.
 * Works alongside AuthContext for Firebase auth state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OfficerProfile, Department, AccountState } from '@copapp/core/types';

interface SessionState {
  // Current user
  officer: OfficerProfile | null;
  department: Department | null;
  accountState: AccountState;

  // Session tracking
  sessionCount: number;
  lastSessionAt: string | null;

  // Loading
  isLoading: boolean;
  isInitialized: boolean;
}

interface SessionActions {
  setOfficer: (officer: OfficerProfile | null) => void;
  setDepartment: (department: Department | null) => void;
  setAccountState: (state: AccountState) => void;
  incrementSessionCount: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: () => void;
  reset: () => void;
}

const initialState: SessionState = {
  officer: null,
  department: null,
  accountState: 'none',
  sessionCount: 0,
  lastSessionAt: null,
  isLoading: true,
  isInitialized: false,
};

export const useSessionStore = create<SessionState & SessionActions>()(
  devtools(
    (set) => ({
      ...initialState,

      setOfficer: (officer) => set({ officer }, false, 'setOfficer'),
      setDepartment: (department) => set({ department }, false, 'setDepartment'),
      setAccountState: (state) => set({ accountState: state }, false, 'setAccountState'),

      incrementSessionCount: () => set((s) => ({
        sessionCount: s.sessionCount + 1,
        lastSessionAt: new Date().toISOString(),
      }), false, 'incrementSession'),

      setLoading: (loading) => set({ isLoading: loading }, false, 'setLoading'),
      setInitialized: () => set({ isInitialized: true, isLoading: false }, false, 'initialized'),
      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'SessionStore' },
  ),
);
